import { useMemo } from "react"
import type { ApolloError } from "@apollo/client"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Link from "@/v2-backoffice-client/components/link"
import UsageReportBarList from "@/v2-backoffice-client/usage/report/UsageReportBarList"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import UsageReportProse from "@/v2-backoffice-client/usage/report/UsageReportProse"
import UsageReportSection from "@/v2-backoffice-client/usage/report/UsageReportSection"
import UsageMcpClientChips from "@/v2-backoffice-client/usage/mcp/UsageMcpClientChips"
import UsageMcpWeekChart from "@/v2-backoffice-client/usage/mcp/UsageMcpWeekChart"
import type { UsageMcpPortfolioQuery } from "@/v2-backoffice-client/usage/mcp/__types__/usage.mcpPortfolio.query"
import {
  formatMcpTool,
  MCP_TOOL_LIST_LIMIT,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.constants"
import {
  deriveMcpToolGroups,
  mergeMcpCounts,
  type UsageMcpCountRow,
} from "@/v2-backoffice-client/usage/mcp/usage.mcp.utils"
import { buildCustomerActivityTabLink } from "@/v2-backoffice-client/customer/customer.links"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"

export type PortfolioMcpDatasetSectionProps = {
  mcp: UsageMcpPortfolioQuery["getUsageMcpForPortfolio"] | null
  loading: boolean
  error?: ApolloError
  /** The Dataset MCP's paragraph(s) of the report's prose. */
  story: string[]
}

const EMPTY_COUNTS: UsageMcpCountRow[] = []

// The Dataset MCP as its own chapter, the way the report had it: a smaller
// product, mostly internal so far, so one section with the key figures, the
// weeks, who uses it and for what. Renders nothing while nobody in scope has
// called it.
function PortfolioMcpDatasetSection(props: PortfolioMcpDatasetSectionProps) {
  const { mcp, loading, error, story } = props

  const summary = mcp?.Summary ?? null
  const customers = mcp?.Customers ?? []
  const tools = useMemo(
    () => mergeMcpCounts(mcp?.Tools ?? EMPTY_COUNTS),
    [mcp?.Tools],
  )
  const groups = useMemo(() => deriveMcpToolGroups(tools), [tools])

  if (error) return <ErrorMessage error={error} />
  if (loading && !mcp) return null
  if (!summary) return null

  const internalTotal = summary.InternalCallsTotal ?? 0

  return (
    <UsageReportSection
      eyebrow="Dataset MCP"
      title="The Dataset MCP"
      lead="The second server lets an AI client create and edit map datasets in a customer's Placepoint project — upload a file, match it to properties, style it, put it on the map."
    >
      <div className={usageKpiGridClass(summary.Denied28d > 0 ? 5 : 4)}>
        <UsageReportKpi
          label="Tool calls"
          value={formatNumber(summary.CallsTotal)}
          sub={[
            `${formatNumber(summary.Calls28d)} in the last 28 days`,
            `${formatNumber(summary.Calls7d)} in the last 7 days`,
          ]}
        />
        <UsageReportKpi
          label="People"
          value={formatNumber(summary.UsersTotal)}
          sub={`${formatNumber(summary.Users28d)} active in the last 28 days`}
        />
        <UsageReportKpi
          label="Customers"
          value={formatNumber(summary.CustomersTotal ?? 0)}
          sub={`${formatNumber(summary.Customers28d ?? 0)} active in the last 28 days`}
        />
        <UsageReportKpi
          label="Internal calls"
          value={formatNumber(internalTotal)}
          sub={`${formatNumber(summary.InternalCalls28d ?? 0)} in the last 28 days`}
        />
        {summary.Denied28d > 0 && (
          <UsageReportKpi
            label="Turned away"
            tone="warning"
            value={formatNumber(summary.Denied28d)}
            sub={`${summary.DeniedUsers28d} ${summary.DeniedUsers28d === 1 ? "person" : "people"}, last 28 days`}
          />
        )}
      </div>

      <div className="usage-report-grid usage-report-grid--two">
        <UsageReportCard title="Week by week">
          <UsageMcpWeekChart
            weeks={mcp?.Weeks ?? []}
            showCustomers
            tableLimit={6}
          />
        </UsageReportCard>

        <div className="flex flex-col gap-4">
          <UsageReportProse title="In short" paragraphs={story} />

          <UsageReportCard
            title="Customers, last 28 days"
            caption="Calls, people and the clients they connect from. A name opens the customer's Dataset MCP page."
          >
            {customers.filter((customer) => customer.Calls28d > 0).length ===
            0 ? (
              <p className="text-xs text-muted-foreground">
                No customer has called it in the last 28 days.
              </p>
            ) : (
              <ul className="flex flex-col gap-2 text-sm">
                {customers
                  .filter((customer) => customer.Calls28d > 0)
                  .map((customer) => (
                    <li
                      key={customer.ExternalCustomerId}
                      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1"
                    >
                      <Link
                        to={buildCustomerActivityTabLink(
                          customer.ExternalCustomerId,
                          "dataset-mcp",
                        )}
                        iconSrc={null}
                        className="truncate font-medium"
                      >
                        {customer.Name}
                      </Link>
                      <span className="flex items-center gap-3 text-xs tabular-nums text-muted-foreground">
                        <span>
                          {formatNumber(customer.Calls28d)} calls ·{" "}
                          {customer.Users28d}{" "}
                          {customer.Users28d === 1 ? "person" : "people"}
                        </span>
                        <UsageMcpClientChips clients={customer.Clients} />
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </UsageReportCard>

          <UsageReportCard
            title="Most used tools"
            caption={
              groups.length > 0
                ? `Mostly ${groups[0].label} (${Math.round(groups[0].share * 100)} % of the calls).`
                : undefined
            }
          >
            <UsageReportBarList
              rows={tools.map((tool) => ({
                key: tool.Key,
                label: formatMcpTool(tool.Key),
                value: tool.Calls28d,
                extra: `${tool.Users28d} ${tool.Users28d === 1 ? "person" : "people"}`,
              }))}
              limit={MCP_TOOL_LIST_LIMIT}
              labelWidth="w-44"
              emptyText="No tool calls in the last 28 days."
            />
          </UsageReportCard>
        </div>
      </div>
    </UsageReportSection>
  )
}

export default PortfolioMcpDatasetSection
